import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Bone, ClipboardCheck, PhoneOutgoing, ChartLine, Stethoscope, Workflow } from 'lucide-react'
import { usePersistedState } from '../../lib/persist.js'
import { Page, PageHeader, Tabs } from '../../kit'
import GapQueue from './implant/GapQueue.jsx'
import ConsultTrack from './implant/ConsultTrack.jsx'
import CallResults from './implant/CallResults.jsx'
import WorkflowGuide from './implant/WorkflowGuide.jsx'

/* ────────────────────────────────────────────────────────────────────────────
 * รายงานรากเทียม — the implant section's first page.
 *
 * The working tabs follow the work, one job each (Ram, 2026-10-29 — the old รายชื่อโทร
 * tab mixed reviewing and calling in one table), then the guideline staff follow:
 *   review   รีวิว                 — who should be called: the lists awaiting review,
 *                                    and apart from them ไม่ต้องโทรตอนนี้ (รอวันนัด ·
 *                                    พักไว้ · รอรอบใหม่). Never claims the call lock.
 *   call     รอโทร                 — พร้อมโทร only: claim the line, call, pick a result
 *   results  ผลการโทร              — the log book of รอโทร: every patient called (Warm Case
 *                                    too), latest result first, every call looked up + charts
 *   consult  ผลเคสปรึกษา           — where each ปรึกษารากเทียม appointment went (a report;
 *            renamed from ติดตามเคสปรึกษา 2026-10-30 — nothing is called from it)
 *   guide    วิธีทำงาน             — the whole workflow as one flowchart (2026-10-29)
 * รีวิว and โทร are one component (GapQueue, `mode`). Their counts are the people
 * awaiting review and the people พร้อมโทร, from the last queue answer this session
 * (api 678 review_view_counts, branch-filtered like the lists) — none until a queue
 * tab has loaded once. The old ?tab=gap (bookmarks, the persisted tab) opens โทร.
 *
 * Spec and every open decision: implant/report.md.
 *
 * The tab lives in the URL (?tab=) so a link can point at any of them, and the two
 * working tabs are also persisted so coming back from a patient page lands where they
 * left off. The guide is never the remembered tab: someone who read it once must not
 * open the page on it every morning instead of the call list.
 * ──────────────────────────────────────────────────────────────────────────── */

// Icons from lucide, the CRM's one icon set — every tab had a fitting one, so no
// second library.
const TABS = [
  { v: 'review', label: 'รีวิว', icon: ClipboardCheck },
  { v: 'call', label: 'รอโทร', icon: PhoneOutgoing },
  { v: 'results', label: 'ผลการโทร', icon: ChartLine },
  { v: 'consult', label: 'ผลเคสปรึกษา', icon: Stethoscope },
  { v: 'guide', label: 'วิธีทำงาน', icon: Workflow },
]
const REMEMBERED = ['review', 'call', 'results', 'consult']
const LEGACY = { gap: 'call' }
const SUBTITLE = {
  review: 'คัดว่าใครควรโทรเสนอรากเทียม · รีวิว 2 ขั้นก่อนส่งให้ทีมโทร · ดูคนที่ไม่ต้องโทรตอนนี้ · ไม่จองสาย',
  call: 'คนไข้ที่ผ่านรีวิวแล้ว พร้อมโทร · จองสายก่อนโทรเพื่อไม่ให้ซ้ำกับแผนกอื่น · เลือกผลการโทรทุกสาย',
  results: 'บันทึกผลการโทรจากแท็บรอโทร ทุกประเภทรวม Warm Case · คนละ 1 แถว ผลล่าสุดขึ้นก่อน · นัดได้ จ่ายแล้ว',
  consult: 'รายงาน: นัดปรึกษารากเทียมแต่ละนัดไปทางไหน · ปิดได้ = จ่ายค่ารากเทียม ฟันปลอม หรือสะพานฟัน · เตรียมตัว = จ่ายขั้นเตรียมหรือนัดขั้นต่อไปแล้ว · ยังไม่ตัดสินใจ · หลุด',
  guide: 'ขั้นตอนตั้งแต่คนไข้ทักเข้ามาจนจ่ายเงิน · ใครทำอะไร · ใครอยู่และไม่อยู่ในลิสต์',
}

// The tab counts outlive a tab switch (module memory; counts only, no patient data).
let lastCounts = null
// A count as words beside the label, like the list buttons — CountBadge caps at 99+, and
// รีวิว holds thousands.
const withCount = (label, n) => (n == null ? label : (
  <>{label}<span className="ml-1.5 font-normal tabular-nums text-slate-400">{Number(n).toLocaleString('en-US')}</span></>
))

export default function Implant() {
  const [params, setParams] = useSearchParams()
  const [savedRaw, setSaved] = usePersistedState('imp:tab', 'call')
  const saved = LEGACY[savedRaw] || savedRaw
  const fromUrl = LEGACY[params.get('tab')] || params.get('tab')
  const tab = TABS.some((t) => t.v === fromUrl) ? fromUrl : (REMEMBERED.includes(saved) ? saved : 'call')
  const [counts, setCounts] = useState(lastCounts)
  useEffect(() => { lastCounts = counts }, [counts])

  // Keep the URL and the persisted value in step without adding a history entry. A
  // click on a tab DOES add one (below), so the browser's Back returns to the last tab.
  useEffect(() => {
    if (REMEMBERED.includes(tab) && tab !== savedRaw) setSaved(tab)
    // keep ?list= (a link from ผลการโทร) when rewriting ?tab=gap → call
    if (params.get('tab') !== tab) setParams((p) => { p.set('tab', tab); return p }, { replace: true })
  }, [tab, savedRaw, params, setSaved, setParams])

  return (
    <Page>
      <PageHeader
        icon={Bone}
        title="รายงานรากเทียม"
        subtitle={SUBTITLE[tab]}
        breadcrumb={[{ label: 'รายงาน', to: '/reports' }, { label: 'รากเทียม' }]}
      >
        <Tabs
          items={TABS.map((t) => ({ key: t.v, icon: t.icon, label: withCount(t.label, counts?.[t.v]) }))}
          value={tab}
          onChange={(v) => setParams({ tab: v })}
        />
      </PageHeader>

      {tab === 'review' ? <GapQueue key="review" mode="review" onCounts={setCounts} />
        : tab === 'call' ? <GapQueue key="call" mode="call" onCounts={setCounts} />
        : tab === 'results' ? <CallResults /> : tab === 'consult' ? <ConsultTrack /> : <WorkflowGuide />}
    </Page>
  )
}
